import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import { AlertFormData, AlertCondition, Parameter } from "../../../types/alert";
import { useGetDeviceParameters } from "../../../queries/parameters";
import { useDeviceStore } from "../../../store/useDeviceStore";
import { useLocationStore } from "../../../store/useLocationStore";
import { useGetDevicesForLocation } from "../../../queries/admin";
import { ParameterFormData } from "../../../components/widgets/table/AddParameterModal";

interface AlertFormProps {
  initialData?: Partial<AlertFormData>;
  onSubmit: (data: AlertFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: "create" | "edit";
}

const initialFormData: AlertFormData = {
  name: "",
  condition: "ABOVE",
  threshold: "",
  thresholdMax: "",
  primaryCooldown: "60",
  cooldown: "1800",
  primaryEmails: [],
  secondaryEmails: [],
  parameterId: "",
};

const conditions: {
  value: AlertCondition;
  label: string;
  description: string;
}[] = [
  {
    value: "ABOVE",
    label: "Above",
    description: "Triggers when parameter value > threshold",
  },
  {
    value: "ABOVE_EQUAL",
    label: "Above or equal to",
    description: "Triggers when parameter value >= threshold",
  },
  {
    value: "BELOW",
    label: "Below",
    description: "Triggers when parameter value < threshold",
  },
  {
    value: "BELOW_EQUAL",
    label: "Below or equal to",
    description: "Triggers when parameter value <= threshold",
  },
  {
    value: "EQUAL",
    label: "Equal to",
    description: "Triggers when parameter value = threshold",
  },
  {
    value: "BETWEEN",
    label: "Between",
    description: "Triggers when threshold ≤ parameter value ≤ max threshold",
  },
];

export default function AlertForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  mode,
}: AlertFormProps) {
  const { selectedDeviceId } = useDeviceStore();
  const { selectedLocationId } = useLocationStore();
  const { data: locationDevices, isLoading: devicesLoading } = useGetDevicesForLocation(selectedLocationId);
  const isCreateMode = mode === "create";

  // Local device selection for this form — defaults to sidebar selection
  const [formDeviceId, setFormDeviceId] = useState(selectedDeviceId);

  const [formData, setFormData] = useState<AlertFormData>({
    ...initialFormData,
    ...initialData,
  });
  const [newPrimaryEmail, setNewPrimaryEmail] = useState("");
  const [newSecondaryEmail, setNewSecondaryEmail] = useState("");
  const [parameters, setParameters] = useState<Parameter[]>([]);

  // When location devices load, default to sidebar device if valid, else first device
  useEffect(() => {
    if (locationDevices && locationDevices.length > 0 && isCreateMode) {
      const isCurrentValid = locationDevices.some((d) => d.id === formDeviceId);
      if (!isCurrentValid) {
        setFormDeviceId(locationDevices[0].id);
      }
    }
  }, [locationDevices]);

  // Fetch device parameters based on form-local device selection
  const { data: deviceParameters, isLoading: parametersLoading } = useGetDeviceParameters(
    formDeviceId,
    !!formDeviceId
  );

  // When device changes in create mode, reset the parameter selection
  const handleDeviceChange = (deviceId: string) => {
    setFormDeviceId(deviceId);
    setFormData((prev) => ({ ...prev, parameterId: "" }));
    setParameters([]);
  };

  useEffect(() => {
    if (deviceParameters) {
      const transformedParams: Parameter[] = deviceParameters.map((param: ParameterFormData) => ({
        id: param.id || "",
        identifier: param.identifier,
        name: param.name,
        unit: param.unit,
        deviceId: param.deviceId,
      }));
      setParameters(transformedParams);

      // Auto-select first parameter if none selected and creating new alert
      if (!formData.parameterId && transformedParams.length > 0 && isCreateMode) {
        setFormData((prev) => ({
          ...prev,
          parameterId: transformedParams[0].id,
        }));
      }
    }
  }, [deviceParameters, formData.parameterId, mode]);

  const updateFormData = (updates: Partial<AlertFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const addPrimaryEmail = () => {
    if (
      newPrimaryEmail.trim() &&
      isValidEmail(newPrimaryEmail) &&
      !formData.primaryEmails.includes(newPrimaryEmail.trim())
    ) {
      updateFormData({
        primaryEmails: [...formData.primaryEmails, newPrimaryEmail.trim()],
      });
      setNewPrimaryEmail("");
    }
  };

  const removePrimaryEmail = (index: number) => {
    updateFormData({
      primaryEmails: formData.primaryEmails.filter((_, i) => i !== index),
    });
  };

  const addSecondaryEmail = () => {
    if (
      newSecondaryEmail.trim() &&
      isValidEmail(newSecondaryEmail) &&
      !formData.secondaryEmails.includes(newSecondaryEmail.trim())
    ) {
      updateFormData({
        secondaryEmails: [...formData.secondaryEmails, newSecondaryEmail.trim()],
      });
      setNewSecondaryEmail("");
    }
  };

  const removeSecondaryEmail = (index: number) => {
    updateFormData({
      secondaryEmails: formData.secondaryEmails.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const isFormValid = () => {
    let valid = !!(
      formData.name.trim() &&
      formData.parameterId &&
      formData.threshold &&
      (formData.condition !== "BETWEEN" || formData.thresholdMax) &&
      formData.primaryEmails.length > 0
    );

    if (formData.condition === "BETWEEN" && formData.threshold !== undefined && formData.thresholdMax !== undefined) {
      if (Number(formData.threshold) >= Number(formData.thresholdMax)) {
        valid = false;
      }
    }

    return valid;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let isValid = isFormValid();

    if (formData.condition === "BETWEEN" && formData.threshold !== undefined && formData.thresholdMax !== undefined) {
      if (Number(formData.threshold) >= Number(formData.thresholdMax)) {
        newErrors.thresholdMax = "Max threshold must be greater than min threshold";
      }
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const selectedParameter = parameters.find((p) => p.id === formData.parameterId);

  const handleKeyPress = (e: React.KeyboardEvent, type: "primary" | "secondary") => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (type === "primary" && newPrimaryEmail.trim() && isValidEmail(newPrimaryEmail)) {
        addPrimaryEmail();
      } else if (
        type === "secondary" &&
        newSecondaryEmail.trim() &&
        isValidEmail(newSecondaryEmail)
      ) {
        addSecondaryEmail();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Alert Details Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Alert Details</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alert name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => updateFormData({ name: e.target.value })}
              placeholder="e.g., High CO2 Alert"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
              required
            />
          </div>
        </div>
      </div>

      {/* Trigger Conditions Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Trigger Conditions
        </h3>
        <div className="space-y-4">
          {/* Device Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Device <span className="text-red-500">*</span>
            </label>
            {devicesLoading ? (
              <div className="animate-pulse h-10 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
            ) : locationDevices && locationDevices.length > 0 ? (
              <div className="relative">
                <select
                  value={formDeviceId}
                  disabled={!isCreateMode}
                  onChange={(e) => handleDeviceChange(e.target.value)}
                  className="w-full h-10 pl-3 pr-10 py-1.5 border-0 rounded-lg bg-white appearance-none ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-200 text-gray-900 text-sm sm:leading-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">Select a device</option>
                  {locationDevices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            ) : (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                No devices found for this location. Add a device first.
              </p>
            )}
          </div>

          {/* Parameter Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Parameter <span className="text-red-500">*</span>
            </label>
            {parametersLoading ? (
              <div className="animate-pulse h-10 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
            ) : parameters.length > 0 ? (
              <div className="relative">
                <select
                  value={formData.parameterId}
                  disabled={!isCreateMode}
                  onChange={(e) => updateFormData({ parameterId: e.target.value })}
                  className="w-full h-10 pl-3 pr-10 py-1.5 border-0 rounded-lg bg-white appearance-none ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-200 text-gray-900 text-sm sm:leading-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">Select a parameter</option>
                  {parameters.map((param) => (
                    <option key={param.id} value={param.id}>
                      {param.name} ({param.identifier}) - {param.unit}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            ) : formDeviceId ? (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                No parameters found for this device. Add parameters first.
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select a device to see its parameters.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Condition <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {conditions.map((condition) => (
                <label
                  key={condition.value}
                  className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <input
                    type="radio"
                    name="condition"
                    value={condition.value}
                    checked={formData.condition === condition.value}
                    onChange={(e) =>
                      updateFormData({
                        condition: e.target.value as AlertCondition,
                      })
                    }
                    className="mt-1 mr-3 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {condition.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {condition.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Threshold {selectedParameter?.unit ? `(${selectedParameter.unit})` : ""}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                value={formData.threshold}
                onChange={(e) => updateFormData({ threshold: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                required
              />
            </div>

            {formData.condition === "BETWEEN" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Threshold {selectedParameter?.unit ? `(${selectedParameter.unit})` : ""}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.thresholdMax}
                  onChange={(e) => updateFormData({ thresholdMax: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                  required
                />
                {formErrors.thresholdMax && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.thresholdMax}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Settings Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Notification Settings
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Cooldown (seconds)
              </label>
              <input
                type="number"
                min="0"
                value={formData.primaryCooldown}
                onChange={(e) => updateFormData({ primaryCooldown: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Cooldown before creating new alert event (default: 60)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Escalation Cooldown (seconds)
              </label>
              <input
                type="number"
                min="0"
                value={formData.cooldown}
                onChange={(e) => updateFormData({ cooldown: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Cooldown before escalation (default: 1800)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Recipients Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Primary Recipients <span className="text-red-500">*</span>
        </h3>
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="email"
              id="primaryEmail"
              placeholder="Add primary recipient email"
              value={newPrimaryEmail}
              onChange={(e) => setNewPrimaryEmail(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, "primary")}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white text-sm"
            />
            <Button
              type="button"
              onClick={addPrimaryEmail}
              disabled={!newPrimaryEmail || !isValidEmail(newPrimaryEmail)}
              size="sm"
            >
              <Icon icon="heroicons:plus" className="w-4 h-4" />
            </Button>
          </div>

          {formData.primaryEmails.length > 0 ? (
            <div className="space-y-2">
              {formData.primaryEmails.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <Icon icon="heroicons:envelope" className="w-4 h-4 text-gray-500 mr-3" />
                    <span className="text-sm text-gray-900 dark:text-white">{email}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePrimaryEmail(index)}
                    className="text-red-500 hover:text-red-700 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Icon icon="heroicons:trash" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <Icon icon="heroicons:user-plus" className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No primary recipients added yet
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                At least one primary recipient is required
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Secondary Recipients Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Secondary Recipients (Escalation)
        </h3>
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="email"
              id="secondaryEmail"
              placeholder="Add secondary recipient email"
              value={newSecondaryEmail}
              onChange={(e) => setNewSecondaryEmail(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, "secondary")}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white text-sm"
            />
            <Button
              type="button"
              onClick={addSecondaryEmail}
              disabled={!newSecondaryEmail || !isValidEmail(newSecondaryEmail)}
              size="sm"
            >
              <Icon icon="heroicons:plus" className="w-4 h-4" />
            </Button>
          </div>

          {formData.secondaryEmails.length > 0 ? (
            <div className="space-y-2">
              {formData.secondaryEmails.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center">
                    <Icon icon="heroicons:envelope" className="w-4 h-4 text-gray-500 mr-3" />
                    <span className="text-sm text-gray-900 dark:text-white">{email}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSecondaryEmail(index)}
                    className="text-red-500 hover:text-red-700 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Icon icon="heroicons:trash" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <Icon icon="heroicons:user-plus" className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No secondary recipients added
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Optional: Add emails for escalated notifications
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !isFormValid()}>
          {isLoading ? (
            <>
              <Icon icon="heroicons:arrow-path" className="w-4 h-4 mr-2 animate-spin" />
              {isCreateMode ? "Creating..." : "Saving..."}
            </>
          ) : isCreateMode ? (
            "Create Alert"
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </form>
  );
}
